import React, { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import { promoteProspect } from 'src/supportPortal/services/patientProspectsApi'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { toast } from 'react-toastify'

/**
 * Manual promote of a Patient Prospect to ROLE_PATIENT. Internal-only —
 * support staff picks the clinic brand here via a typeahead Autocomplete
 * (no free-text id any more), then optionally pre-assigns a mentor that
 * actually serves at the chosen brand. Backend creates the PatientInfo
 * with {@code requiresValidation=true} + {@code prospectInfoId} back-link.
 *
 * Brand list comes from the existing
 * {@code GET /api/secure/cc/support/clinic-brand/list} endpoint, mentor
 * list from {@code GET /api/secure/cc/support/mentors/list?sendAssociatedChambers=true}
 * (filtered client-side to mentors who appear in any ChamberRoom for the
 * picked brand). We don't reuse `usePatientReferenceData` here because
 * it also pulls the (much larger) clinic-locations list this dialog
 * doesn't need, and re-runs network calls every time it mounts.
 */
export default function PromoteProspectDialog({ open, prospect, onClose, onPromoted }) {
  const [brands, setBrands] = useState([])
  const [brandsLoading, setBrandsLoading] = useState(false)
  const [brandsError, setBrandsError] = useState('')

  const [mentors, setMentors] = useState([])
  const [chamberRooms, setChamberRooms] = useState([])
  const [mentorsLoading, setMentorsLoading] = useState(false)
  const [mentorsError, setMentorsError] = useState('')

  const [selectedBrand, setSelectedBrand] = useState(null)
  const [selectedMentor, setSelectedMentor] = useState(null)
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // Reset transient state on every open so a previous run doesn't leak.
  useEffect(() => {
    if (!open) return
    setSelectedBrand(null)
    setSelectedMentor(null)
    setNotes('')
    setError('')
  }, [open])

  // Fetch brands the first time the dialog opens (cached afterwards).
  useEffect(() => {
    if (!open) return
    if (brands.length > 0 || brandsLoading) return
    let cancelled = false
    setBrandsLoading(true)
    setBrandsError('')
    getSPDataWithParams('/api/secure/cc/support/clinic-brand/list', {})
      .then((res) => {
        if (cancelled) return
        const payload =
          res?.data?.data?.['clinic-brand'] ||
          res?.data?.data?.clinicBrands ||
          res?.data?.['clinic-brand'] ||
          res?.data?.data ||
          res?.data ||
          []
        const list = Array.isArray(payload) ? payload : []
        list.sort((a, b) => (a?.brandName || '').localeCompare(b?.brandName || ''))
        setBrands(list)
      })
      .catch(() => {
        if (cancelled) return
        setBrandsError('Could not load clinic brands. Refresh and try again.')
      })
      .finally(() => {
        if (cancelled) return
        setBrandsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, brands.length, brandsLoading])

  // Fetch mentors + chamberRooms once on first open. Filtering by brand
  // happens client-side via a memo on `selectedBrand`.
  useEffect(() => {
    if (!open) return
    if (mentors.length > 0 || mentorsLoading) return
    let cancelled = false
    setMentorsLoading(true)
    setMentorsError('')
    getSPDataWithParams('/api/secure/cc/support/mentors/list', {
      sendAssociatedChambers: true,
      mentorEventTypes: 'therapy-session,consultation',
    })
      .then((res) => {
        if (cancelled) return
        const data = res?.data?.data || res?.data || {}
        const mentorList = Array.isArray(data.mentor)
          ? data.mentor
          : Array.isArray(data.mentors)
            ? data.mentors
            : []
        const chamberList = Array.isArray(data.chamberRoom)
          ? data.chamberRoom
          : Array.isArray(data.chamberRooms)
            ? data.chamberRooms
            : []
        setMentors(mentorList)
        setChamberRooms(chamberList)
      })
      .catch(() => {
        if (cancelled) return
        setMentorsError('Could not load mentors. Mentor selection may be unavailable.')
      })
      .finally(() => {
        if (cancelled) return
        setMentorsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, mentors.length, mentorsLoading])

  // mentorId → Set<clinicBrandId> derived from chamberRoom associations.
  const mentorBrandsMap = useMemo(() => {
    const map = new Map()
    for (const room of chamberRooms || []) {
      const brandId = room?.clinicBrandId
      if (!brandId) continue
      const combos = Array.isArray(room?.mentorsAndAuditors) ? room.mentorsAndAuditors : []
      for (const c of combos) {
        const mid = c?.mentorId
        if (!mid) continue
        const set = map.get(mid) || new Set()
        set.add(brandId)
        map.set(mid, set)
      }
    }
    return map
  }, [chamberRooms])

  // Mentors who serve at the picked brand. When no brand is picked, the
  // mentor field is hidden — staff hasn't told us where the patient is
  // going yet, so showing 1000+ mentors is noise.
  const mentorsForBrand = useMemo(() => {
    if (!selectedBrand?.id) return []
    return (mentors || []).filter((m) => {
      const id = m?.id || m?._id
      if (!id) return false
      const brandSet = mentorBrandsMap.get(id)
      return brandSet ? brandSet.has(selectedBrand.id) : false
    })
  }, [mentors, mentorBrandsMap, selectedBrand?.id])

  // Reset mentor selection when brand changes — the previous mentor
  // probably doesn't serve at the new brand.
  useEffect(() => {
    setSelectedMentor(null)
  }, [selectedBrand?.id])

  const brandLabel = (b) => b?.brandName || b?.title || b?.id || ''
  const mentorLabel = (m) => {
    const first = m?.firstName || ''
    const last = m?.lastName || ''
    const name = `${first} ${last}`.trim()
    if (name) return name
    return m?.slug || m?.id || ''
  }

  const handleSubmit = async () => {
    setError('')
    if (!selectedBrand?.id) {
      setError('Please pick a clinic brand.')
      return
    }
    if (!prospect?.userId) {
      setError('Missing userId on this prospect.')
      return
    }
    setBusy(true)
    try {
      const res = await promoteProspect(prospect.userId, {
        clinicBrandId: selectedBrand.id,
        mentorId: selectedMentor?.id || null,
        notes: notes.trim() || null,
      })
      if (res?.status >= 200 && res?.status < 300) {
        toast.success(`Promoted to patient at ${brandLabel(selectedBrand)}.`)
        onPromoted?.(res.data)
        onClose?.()
      } else {
        const msg = res?.response?.data?.message || res?.data?.message || 'Promotion failed.'
        setError(msg)
      }
    } catch (e) {
      setError(e?.message || 'Promotion failed.')
    } finally {
      setBusy(false)
    }
  }

  const subtitle = useMemo(() => {
    if (!prospect) return ''
    const bits = []
    if (prospect.name) bits.push(prospect.name)
    if (prospect.mobile) bits.push(prospect.mobile)
    if (prospect.pathway) bits.push(prospect.pathway)
    if (prospect.specialtyTag) bits.push(`(${prospect.specialtyTag})`)
    return bits.join(' · ')
  }, [prospect])

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Promote to Patient</DialogTitle>
      <DialogContent dividers>
        {subtitle ? (
          <div style={{ marginBottom: 12, fontSize: 14, color: '#475569' }}>{subtitle}</div>
        ) : null}

        <Autocomplete
          options={brands}
          loading={brandsLoading}
          getOptionLabel={brandLabel}
          isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
          value={selectedBrand}
          onChange={(_, b) => setSelectedBrand(b)}
          autoHighlight
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Clinic brand"
              autoFocus
              margin="dense"
              helperText={brandsError || 'Pick the brand the patient should be created at.'}
              error={!!brandsError}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {brandsLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {selectedBrand?.id ? (
          <Autocomplete
            options={mentorsForBrand}
            loading={mentorsLoading}
            getOptionLabel={mentorLabel}
            isOptionEqualToValue={(opt, val) => (opt?.id || opt?._id) === (val?.id || val?._id)}
            value={selectedMentor}
            onChange={(_, m) => setSelectedMentor(m)}
            autoHighlight
            noOptionsText={
              mentorsError
                ? 'Mentors couldn’t be loaded.'
                : 'No mentors found for this brand.'
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Mentor (optional)"
                margin="dense"
                helperText={
                  mentorsError ||
                  `Pre-assign a practitioner if you already know who'll see them. ${
                    mentorsForBrand.length
                  } available at ${brandLabel(selectedBrand)}.`
                }
                error={!!mentorsError}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {mentorsLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        ) : null}

        <TextField
          label="Notes"
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          margin="dense"
          multiline
          minRows={2}
          helperText="Audit-only. What you considered when promoting."
        />
        {error ? (
          <div style={{ color: '#b42318', marginTop: 8, fontSize: 13 }}>{error}</div>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={busy || !selectedBrand?.id}
        >
          {busy ? 'Promoting…' : 'Promote'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
